<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/searchfield';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '@react-spectrum/searchfield/package.json';

```jsx import
import {Flex} from '@react-spectrum/layout';
import {SearchField} from '@react-spectrum/searchfield';
import User from '@spectrum-icons/workflow/User';
```

---
category: Forms
keywords: [search field, input]
---

# SearchField

<p>{docs.exports.SearchField.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['SearchField']} />

## Example

```tsx example
function Example() {
  let [submittedText, setSubmittedText] = React.useState();

  return (
    <>
      <SearchField
        label="Search"
        placeholder="Enter text"
        onSubmit={setSubmittedText} />
      <p>Submitted text: {submittedText}</p>
    </>
  );
}
```

## Value

A SearchField's `value` is empty by default, but an initial, uncontrolled, value can be provided using the `defaultValue` prop.
Alternatively, a controlled value can be provided using the `value` prop.

```tsx example
function Example() {
  let [searchValue, setSearchValue] = React.useState('puppies');
  return (
    <Flex gap="size-300">
      <SearchField
        defaultValue="puppies"
        label="Search (uncontrolled)" />

      <SearchField
        value={searchValue}
        onChange={setSearchValue}
        label="Search (controlled)" />
    </Flex>
  );
}
```

Placeholder text that describes the expected value or formatting for the SearchField can be provided using the `placeholder` prop.
Placeholder text will only appear when the SearchField is empty, and should not be used as a substitute for labeling the component with a visible label.

```tsx example
<SearchField placeholder="e.g. Sally Jones" label="Search for user" />
```

## Labeling

A visual label should be provided for the SearchField using the `label` prop. If the SearchField is required, the `isRequired` and `necessityIndicator` props can be used to show a required state.

```tsx example
<Flex gap="size-300" wrap>
  <SearchField label="Search" />
  <SearchField label="Search" isRequired necessityIndicator="icon" />
  <SearchField label="Search" isRequired necessityIndicator="label" />
  <SearchField label="Search" necessityIndicator="label" />
</Flex>
```

### Accessibility

If a visible label isn't specified, an `aria-label` must be provided to the SearchField for
accessibility. If the field is labeled by a separate element, an `aria-labelledby` prop must be provided using
the `id` of the labeling element instead.

### Internationalization

In order to internationalize a SearchField, a localized string should be passed to the `label` or `aria-label` prop.
When the `necessityIndicator` prop is set to `"label"`, a localized string will be provided for `"(required)"` or `"(optional)"` automatically.

## Events

The most commonly used handlers for events in SearchField are the:
- `onChange` prop which is triggered whenever the value is edited by the user.
- `onSubmit` prop which is triggered whenever the value is submitted by the user.
- `onClear` prop which is triggered whenever the value is cleared by the user.

The example below uses `onChange`, `onSubmit`, and `onClear` to update two separate elements with the text entered into the SearchField.

```tsx example
function Example() {
  let [currentText, setCurrentText] = React.useState('');
  let [submittedText, setSubmittedText] = React.useState('');

  return (
    <div>
      <SearchField
        placeholder="Enter some text"
        onClear={() => setCurrentText('')}
        onChange={setCurrentText}
        onSubmit={setSubmittedText}
        label="Your text"
        value={currentText}
      />
      <pre>Mirrored text: {currentText}</pre>
      <pre>Submitted text: {submittedText}</pre>
    </div>
  );
}
```

## Props

<PropTable component={docs.exports.SearchField} links={docs.links} />

## Visual options

### Quiet

```tsx example
<SearchField label="Search" placeholder="Email Address" isQuiet />
```

### Disabled

```tsx example
<SearchField label="Search" placeholder="Email Address" isDisabled />
```

### Read only

The `isReadOnly` boolean prop makes the SearchField's text content immutable. Unlike `isDisabled` the SearchField remains focusable
and the contents can still be copied. See [the MDN docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly) for more information.

```tsx example
<SearchField label="Search" defaultValue="abc@adobe.com" isReadOnly />
```

### Label alignment and position

By default, the label is positioned above the SearchField. The `labelPosition` prop can be used to position the label to the side. The `labelAlign` prop can be used to align the label as "start" or "end". For left-to-right (LTR) languages, "start" refers to the left most edge of the SearchField and "end" refers to the right most edge. For right-to-left (RTL) languages, this is flipped.

```tsx example
<SearchField label="Search" labelPosition="side" labelAlign="end" />
```

### Help text
[View guidelines](https://spectrum.adobe.com/page/help-text/#Usage-guidelines)

Both a description and an error message can be supplied to a ComboBox. The description is always visible unless the `validationState` is “invalid” and an error message is provided. The error message can be used to help the user fix their input quickly and should be specific to the detected error. All strings should be localized.

```tsx example
function Example() {
  let [value, setValue] = React.useState('0');
  let isValid = React.useMemo(() => /^\d$/.test(value), [value]);

  return (
    <SearchField
      validationState={isValid ? 'valid' : 'invalid'}
      value={value}
      onChange={setValue}
      label="Search your favorite number"
      maxLength={1}
      description="Enter a single digit number."
      errorMessage={value === '' ? 'Empty input not allowed.' : 'Single digit numbers are 0-9.'}
    />
  );
}
```

### Custom width

```tsx example
<SearchField label="Search" width="size-3600" />
```

### Custom icon
The icon in the SearchField can be changed to suit the theme of a search. For instance, if the SearchField was for searching users, an icon relating to that would help convey meaning. See the [Icons page](./Icon.html) for more.
```tsx example
<SearchField label="Search for users" icon={<User />} />
```
